import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Briefcase, Clock, Users, Building2, Settings as SettingsIcon, LogOut, Radio, Sun, Moon, X, ClipboardList, Receipt, ReceiptText, FileText, PieChart, Coins, BookOpen, Warehouse, CalendarRange, Camera, Shield, Check, ChevronDown, HardHat, Compass, Package, Truck, UserRound } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth, isManager, isSubcontractor, isAccountant, isDesigner } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { NotificationBell } from "@/components/NotificationBell";
import { Avatar } from "@/components/shared/Avatar";
import { AppVersionLine } from "@/components/shared/UpdateAvailable";
import { DocUploadOverlay } from "@/components/doc-upload/DocUploadOverlay";
import OwnerGuide from "@/components/OwnerGuide";
import { useDocPicker } from "@/components/shared/DocPicker";
import { useDocDrop, isDocFile } from "@/components/doc-upload/DocDrop";
import { storage } from "@/lib/storage";
import { watchGlass } from "@/lib/glass";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetClose } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { roleLabel } from "@/lib/roles";
import { guideLabel, hasGuide } from "@/lib/ownerGuide";
import MobileHeader from "@/components/mobile/MobileHeader";
import MobileBottomBar, { ShiftHoldButton, CenterTapButton } from "@/components/mobile/MobileBottomBar";
import MenuB from "@/components/mobile/MenuB";
import { SideMenu, SideTip, CREATE_ICONS } from "@/components/layout/SideMenu";
import { NewTaskDialog } from "@/components/tasks/NewTaskDialog";
import { shiftStateFrom, elapsedHm, hoursLabel } from "@/components/mobile/shiftState";

// Navigace: vedení má rozbalovací skupiny (Provoz / Plánování / Firma),
// dělník a subdodavatel plochý krátký seznam. Nastavení a Superadmin jsou u všech rolí dole u profilu (245).
const navFor = (user) => {
  // Účetní vidí jedinou věc — Ekonomiku. Žádné zakázky, docházku ani tým.
  if (isAccountant(user)) {
    return [
      { to: "/ekonomika", label: "Přehled", icon: PieChart, testid: "nav-eco-overview", end: true },
      { to: "/ekonomika/doklady", label: "Doklady", icon: FileText, testid: "nav-economics", activePrefix: "/ekonomika/doklady" },
      { to: "/ekonomika/platby", label: "Platby", icon: Coins, testid: "nav-payments", activePrefix: "/ekonomika/platby" },
      { to: "/ekonomika/pokladna", label: "Pokladna", icon: Coins, testid: "nav-cashbox", activePrefix: "/ekonomika/pokladna" },
      { to: "/ekonomika/naskladneni", label: "Naskladnění", icon: Warehouse, testid: "nav-stock", activePrefix: "/ekonomika/naskladneni" },
      { to: "/ekonomika/rezie", label: "Režie", icon: Receipt, testid: "nav-overhead", activePrefix: "/ekonomika/rezie" },
    ];
  }
  if (isManager(user)) {
    return [
      { to: "/", label: "Přehled", icon: LayoutDashboard, testid: "nav-dashboard" },
      // 335: Tabuli vidí celé vedení včetně asistent/ky (kdo je teď v práci).
      { to: "/tabule", label: "Tabule", icon: Radio, testid: "nav-liveboard" },
      { group: "provoz", label: "Provoz", icon: HardHat, testid: "nav-group-provoz", items: [
        { to: "/zakazky", label: "Zakázky", icon: Briefcase, testid: "nav-projects" },
        { to: "/dochazka", label: "Docházka", icon: Clock, testid: "nav-attendance" },
        { to: "/denik", label: "Deník", icon: BookOpen, testid: "nav-diary" },
        { to: "/galerie", label: "Galerie", icon: Camera, testid: "nav-gallery" },
        { to: "/sklad", label: "Sklad", icon: Warehouse, testid: "nav-warehouse" },
        { to: "/objednavky", label: "Objednávky", icon: Package, testid: "nav-orders" },
      ] },
      { group: "planovani", label: "Plánování", icon: CalendarRange, testid: "nav-group-planovani", items: [
        { to: "/planovani", label: "Gantt", icon: CalendarRange, testid: "nav-planning" },
        { to: "/ukoly", label: "Úkoly", icon: ClipboardList, testid: "nav-tasks" },
      ] },
      { group: "firma", label: "Firma", icon: Building2, testid: "nav-group-firma", items: [
        { to: "/ekonomika/doklady", label: "Ekonomika", icon: PieChart, testid: "nav-economics", activePrefix: "/ekonomika" },
        { to: "/tym", label: "Tým", icon: Users, testid: "nav-team" },
        { to: "/investori", label: "Investoři", icon: Building2, testid: "nav-investors" },
        { to: "/dodavatele", label: "Dodavatelé", icon: Truck, testid: "nav-suppliers" },
      ] },
    ];
  }
  if (isDesigner(user)) {
    // Projektant (173): svoje zakázky, deník, Gantt (čtení) a úkoly. Žádná docházka ani peníze.
    return [
      { to: "/", label: "Přehled", icon: LayoutDashboard, testid: "nav-dashboard" },
      { to: "/zakazky", label: "Zakázky", icon: Briefcase, testid: "nav-projects" },
      { to: "/denik", label: "Deník", icon: BookOpen, testid: "nav-diary" },
      { to: "/planovani", label: "Gantt", icon: CalendarRange, testid: "nav-planning" },
      { to: "/ukoly", label: "Úkoly", icon: ClipboardList, testid: "nav-tasks" },
    ];
  }
  if (isSubcontractor(user)) {
    // Subdodavatel: přehled, úkoly, docházka (jen svá) a vlastní faktury.
    return [
      { to: "/", label: "Přehled", icon: LayoutDashboard, testid: "nav-dashboard" },
      { to: "/ukoly", label: "Úkoly", icon: ClipboardList, testid: "nav-tasks" },
      { to: "/dochazka", label: "Docházka", icon: Clock, testid: "nav-attendance" },
      { to: "/moje-faktury", label: "Moje faktury", icon: ReceiptText, testid: "nav-my-invoices" },
    ];
  }
  if (user?.role === "WAREHOUSE") {
    // Skladník: přehled, sklad a docházka. Ekonomiku nevidí.
    return [
      { to: "/", label: "Přehled", icon: LayoutDashboard, testid: "nav-dashboard" },
      { to: "/sklad", label: "Sklad", icon: Warehouse, testid: "nav-warehouse" },
      { to: "/objednavky", label: "Dodávky", icon: Package, testid: "nav-orders" },
      { to: "/dochazka", label: "Docházka", icon: Clock, testid: "nav-attendance" },
    ];
  }
  return [
    { to: "/", label: "Přehled", icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/dochazka", label: "Docházka", icon: Clock, testid: "nav-attendance" },
    { to: "/ukoly", label: "Úkoly", icon: ClipboardList, testid: "nav-tasks" },
    { to: "/zakazky", label: "Zakázky", icon: Briefcase, testid: "nav-projects" },
    { to: "/galerie", label: "Galerie", icon: Camera, testid: "nav-gallery" },
    { to: "/objednavky", label: "Dodávky", icon: Package, testid: "nav-orders" },
  ];
};




// Přepínač firem (multi-firma): zobrazí se jen, když má e-mail víc aktivních členství.
// Přepnutí = nová session pro vybrané členství + tvrdý reload (čistý stav klienta).
// variant "menu" (desktop) = řádky v nabídce pod jménem — ta je dostupná i když
// je sidebar sbalený; "drawer" (mobil) = výrazné řádky přes celou šířku.
function CompanySwitcher({ variant = "sidebar" }) {
  const { applyAuth } = useAuth();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false); // drawer: ostatní firmy sbalené (136/D)
  useEffect(() => {
    api.get("/auth/memberships").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);
  if (!items || items.length < 2) return null;
  const switchTo = async (mid) => {
    try {
      const { data } = await api.post("/auth/switch", { membership_id: mid });
      applyAuth(data);
      window.location.assign("/");   // tvrdý reload = čistý stav klienta (žádná data z předchozí firmy)
    } catch {
      toast.error("Přepnutí firmy se nezdařilo. Zkuste to prosím znovu.");
    }
  };

  if (variant === "drawer") {
    // Jedna řádka = aktuální firma; ostatní se rozbalí na klik (136/D).
    const cur = items.find((m) => m.current) || items[0];
    const others = items.filter((m) => m !== cur);
    const othersUnread = others.reduce((s, m) => s + (m.unread || 0), 0);
    return (
      <div className="mt-3 flex flex-col gap-1.5" data-testid="company-switcher">
        <button type="button" onClick={() => setExpanded((e) => !e)} aria-expanded={expanded} data-testid="company-current"
          className="flex items-center gap-2 text-left text-sm px-3 py-2.5 rounded-[12px] border border-brand bg-brand/10 text-foreground font-bold">
          <Building2 size={16} className="shrink-0 text-brand" />
          <span className="min-w-0 flex-1 truncate">{cur.company_name}<span className="opacity-70 font-medium"> · {cur.role_label}</span></span>
          {!expanded && othersUnread > 0 && (
            <span data-testid="company-others-unread" className="shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-brand text-white text-[11px] font-bold flex items-center justify-center tabular-nums">
              {othersUnread > 99 ? "99+" : othersUnread}
            </span>
          )}
          <ChevronDown size={15} className={`shrink-0 text-muted-foreground transition-transform motion-reduce:transition-none ${expanded ? "rotate-180" : ""}`} />
        </button>
        {expanded && others.map((m, i) => (
          <button key={m.membership_id} onClick={() => switchTo(m.membership_id)}
            data-testid={`company-switch-${i}`}
            className="flex items-center gap-2 text-left text-sm px-3 py-2.5 rounded-[12px] border border-border text-muted-foreground hover:border-brand hover:text-foreground transition-colors">
            <Building2 size={16} className="shrink-0 text-brand" />
            <span className="min-w-0 truncate">{m.company_name}<span className="opacity-70 font-medium"> · {m.role_label}</span></span>
            {m.unread > 0 && (
              <span data-testid={`company-unread-${i}`} className="ml-auto shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-brand text-white text-[11px] font-bold flex items-center justify-center tabular-nums">
                {m.unread > 99 ? "99+" : m.unread}
              </span>
            )}
          </button>
        ))}
      </div>
    );
  }

  // Varianta do nabídky pod jménem. Řádky jsou DropdownMenuItem, ne obyčejná
  // tlačítka — jinak by je nabídka nebrala do klávesové navigace.
  return (
    <div data-testid="company-switcher">
      <DropdownMenuSeparator />
      <DropdownMenuLabel className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground py-1.5">
        Přepnout účet
      </DropdownMenuLabel>
      {items.map((m, i) => (
        <DropdownMenuItem key={m.membership_id} disabled={m.current} data-testid={`company-switch-${i}`}
          onClick={() => !m.current && switchTo(m.membership_id)}
          className="py-2.5">
          <Building2 size={16} className="mr-2 shrink-0 text-brand" />
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold truncate">{m.company_name}</span>
            <span className="block text-xs text-muted-foreground truncate">{m.role_label}</span>
          </span>
          {m.current ? (
            <Check size={15} className="ml-2 shrink-0 text-brand" />
          ) : m.unread > 0 ? (
            <span data-testid={`company-unread-${i}`}
              className="ml-2 shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-brand text-white text-[11px] font-bold flex items-center justify-center tabular-nums">
              {m.unread > 99 ? "99+" : m.unread}
            </span>
          ) : null}
        </DropdownMenuItem>
      ))}
    </div>
  );
}

export function Layout({ children }) {
  const { user, company, logout, access } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const location = useLocation();
  const manager = isManager(user);
  const accountant = isAccountant(user);
  const items = navFor(user);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuSearch, setMenuSearch] = useState(false);   // otevřeno přes „Hledat“ = rovnou psát
  const [profileOpen, setProfileOpen] = useState(false);
  const [fabOpen, setFabOpen] = useState(false);

  const resendVerification = async () => {
    try {
      await api.post("/auth/resend-verification");
      toast.success(`Odkaz jsme poslali na ${user?.email}`);
    } catch {
      toast.error("Odeslání se nezdařilo. Zkuste to prosím znovu.");
    }
  };

  const [docUpload, setDocUpload] = useState(null); // null | { type: "faktura" | "uctenka", file }
  // 184: klepnutí na Fakturu / Účtenku rovnou otevře výběr fotky / souboru.
  const docPicker = useDocPicker({ onPick: (file, type) => setDocUpload({ type, file }) });
  const [shiftUi, setShiftUi] = useState(null);   // stav mé směny pro středové tlačítko (194)
  const [, setTick] = useState(0);
  const [kbdOpen, setKbdOpen] = useState(false);   // na mobilu je otevřená klávesnice

  // Sbalený sidebar (jen ikony) — přání majitele: víc místa pro obsah na desktopu.
  // 382: na slabém počítači sklo samo vypne, když se při posouvání nestíhá (lib/glass.js)
  useEffect(() => watchGlass(), []);
  // 424: klepnutí na upozornění na displeji, když už je aplikace otevřená — service worker
  // pošle adresu úkolu / směny a aplikace na ni přejde bez načtení stránky
  useEffect(() => {
    const sw = typeof navigator !== "undefined" && navigator.serviceWorker;
    if (!sw) return undefined;
    const onMsg = (e) => {
      if (e.data?.type !== "pp-open" || !e.data.url) return;
      try { const u = new URL(e.data.url, window.location.origin); if (u.origin === window.location.origin) nav(u.pathname + u.search); } catch { /* neplatná adresa */ }
    };
    sw.addEventListener("message", onMsg);
    return () => sw.removeEventListener("message", onMsg);
  }, [nav]);
  // 384: nová stránka = znovu pustit zaostření bloků (filtry v ?query animaci nespouštějí)
  const pageRef = useRef(null);
  const pagePath = location.pathname;
  useEffect(() => {
    const el = pageRef.current;
    if (!el) return;
    el.classList.remove("pp-page-in"); void el.offsetWidth; el.classList.add("pp-page-in");
  }, [pagePath]);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("pp-sidebar-collapsed") === "1"; } catch { return false; }
  });
  const toggleCollapsed = () => setCollapsed((c) => {
    try { localStorage.setItem("pp-sidebar-collapsed", c ? "0" : "1"); } catch { /* noop */ }
    return !c;
  });
  const isItemActive = (it) => it.activePrefix
    ? location.pathname.startsWith(it.activePrefix)
    : (it.to === "/" ? location.pathname === "/" : location.pathname.startsWith(it.to));

  // Středové tlačítko mobilní lišty ukazuje stav mé směny (194) — i vedení, které
  // se do práce také přihlašuje. Účetní a projektant docházku nevedou.
  const tracksAttendance = !accountant && !isDesigner(user);
  // 215: stav směny se mění jen přes MyShiftBlock (událost pp-shift-changed) a automatické
  // ukončení na serveru — proto ne 2 requesty při každém přechodu stránky, ale nejvýš jednou za minutu
  // (a hned po návratu na kartu).
  const shiftFetchedAt = useRef(0);
  const fetchShiftRef = useRef(null);
  useEffect(() => {
    if (!tracksAttendance) return;
    let alive = true;
    const fetchShift = () => {
      shiftFetchedAt.current = Date.now();
      return Promise.all([
        api.get("/me/overview").catch(() => null),
        api.get("/daysheet/current").catch(() => null),
      ]).then(([o, d]) => { if (alive && (o || d)) setShiftUi(shiftStateFrom(o?.data, d?.data)); });
    };
    fetchShiftRef.current = fetchShift;
    const stale = () => { if (document.visibilityState === "visible" && Date.now() - shiftFetchedAt.current > 60000) fetchShift(); };
    fetchShift();
    window.addEventListener("pp-shift-changed", fetchShift);
    document.addEventListener("visibilitychange", stale);
    return () => {
      alive = false; fetchShiftRef.current = null;
      window.removeEventListener("pp-shift-changed", fetchShift);
      document.removeEventListener("visibilitychange", stale);
    };
  }, [tracksAttendance]);
  useEffect(() => {
    if (fetchShiftRef.current && Date.now() - shiftFetchedAt.current > 60000) fetchShiftRef.current();
  }, [location.pathname]);
  // běžící čas na tlačítku (stačí po půl minutě)
  useEffect(() => {
    if (shiftUi?.state !== "working") return;
    const id = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, [shiftUi?.state]);
  // Při psaní (otevřená klávesnice) spodní lišta zajede — na Androidu by jinak
  // vyjela nad klávesnici a zakrývala pole.
  useEffect(() => {
    const isField = (el) => !!el && ((el.tagName === "INPUT" && !["checkbox", "radio", "button", "submit", "file", "range", "color", "reset", "image"].includes(el.type))
      || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || el.isContentEditable);
    const onIn = (e) => { if (isField(e.target)) setKbdOpen(true); };
    const onOut = () => setTimeout(() => setKbdOpen(isField(document.activeElement)), 80);
    document.addEventListener("focusin", onIn);
    document.addEventListener("focusout", onOut);
    return () => { document.removeEventListener("focusin", onIn); document.removeEventListener("focusout", onOut); };
  }, []);

  const doLogout = () => { logout(); };

  // Bottom bar: two links, center FAB, two links.
  const bLeft = accountant
    ? [{ to: "/ekonomika/doklady", label: "Doklady", icon: FileText, testid: "nav-economics" },
       { to: "/ekonomika/platby", label: "Platby", icon: Coins, testid: "nav-payments" }]
    : isSubcontractor(user)
    ? [{ to: "/", label: "Přehled", icon: LayoutDashboard, testid: "nav-dashboard" },
       { to: "/moje-faktury", label: "Faktury", icon: ReceiptText, testid: "nav-my-invoices" }]
    : user?.role === "WAREHOUSE"   // 202: skladník má na liště Sklad, ne Zakázky
    ? [{ to: "/", label: "Přehled", icon: LayoutDashboard, testid: "nav-dashboard" },
       { to: "/sklad", label: "Sklad", icon: Warehouse, testid: "nav-warehouse" }]
    : [{ to: "/", label: "Přehled", icon: LayoutDashboard, testid: "nav-dashboard" },
       { to: "/zakazky", label: "Zakázky", icon: Briefcase, testid: "nav-projects" }];
  const designer = isDesigner(user);
  const bRight = accountant
    ? [{ to: "/ekonomika", label: "Přehled", icon: PieChart, testid: "nav-eco-overview" },
       { to: "/ekonomika/rezie", label: "Režie", icon: Receipt, testid: "nav-overhead" }]
    : designer
    ? [{ to: "/ukoly", label: "Úkoly", icon: ClipboardList, testid: "nav-tasks" },
       { to: "/planovani", label: "Gantt", icon: CalendarRange, testid: "nav-planning" }]
    : [{ to: "/ukoly", label: "Úkoly", icon: ClipboardList, testid: "nav-tasks" },
       { to: "/dochazka", label: "Docházka", icon: Clock, testid: "nav-attendance" }];

  const uploader = manager || accountant;   // kdo nahrává doklady (sken v horní liště)

  const czPlural = (n, one, few, many) => (n === 1 ? one : n < 5 ? few : many);
  // 334: PDF / fotku dokladu stačí na PC přetáhnout z pošty nebo složky kamkoli do okna.
  // Víc souborů naráz → fronta: další se otevře, jakmile se předchozí uloží nebo zavře.
  const dropQueue = useRef([]);
  const dropTotal = useRef(0);
  const nextDropped = () => {
    const f = dropQueue.current.shift();
    if (!f) { dropTotal.current = 0; setDocUpload(null); return; }
    setDocUpload({ type: "faktura", file: f });
    toast(`Další doklad (${dropTotal.current - dropQueue.current.length} z ${dropTotal.current})`);
  };
  const onDroppedFiles = (files) => {
    const ok = files.filter(isDocFile);
    if (!ok.length) { toast.error("Tohle neumíme přečíst — přetáhněte PDF nebo fotku dokladu."); return; }
    const skipped = files.length - ok.length;
    if (skipped) toast(`Vynechali jsme ${skipped} ${czPlural(skipped, "soubor", "soubory", "souborů")} — umíme PDF a fotky.`);
    if (docUpload) {
      dropQueue.current.push(...ok);
      dropTotal.current = (dropTotal.current || 1) + ok.length;
      const n = dropQueue.current.length;
      toast(`Ve frontě ${n === 1 ? "čeká" : n < 5 ? "čekají" : "čeká"} ${n} ${czPlural(n, "doklad", "doklady", "dokladů")} — ${n === 1 ? "otevře" : "otevřou"} se po uložení.`);
      return;
    }
    dropQueue.current = ok.slice(1);
    dropTotal.current = ok.length;
    setDocUpload({ type: "faktura", file: ok[0] });
    if (ok.length > 1) toast(`Doklad 1 z ${ok.length} — další se otevře po uložení.`);
  };
  // 386: na Objednávkách přetažené PDF nečteme jako fakturu — objednávka by se jinak stala nákladem.
  const onOrders = location.pathname.startsWith("/objednavky");
  const docDrop = useDocDrop({ enabled: uploader, acceptOverDialog: !!docUpload,
    onFiles: onOrders ? () => toast("Přílohu k objednávce přidejte v objednávce tlačítkem Přiložit soubor. Faktury nahrávejte v Ekonomice › Doklady.") : onDroppedFiles,
    hint: onOrders ? { title: "Tady se faktury nenahrávají", text: "Přílohu k objednávce přidejte v objednávce tlačítkem Přiložit soubor." } : null });

  // Středové tlačítko (194): docházka se potvrzuje podržením a otevře to, co dnes
  // dělá Přehled (/?shift=1 → výběr stavby / odhlášení / výkaz). Vedení má směnu
  // na záložce Osobní. Účetní = doklad, projektant = deník (173) — bez podržení.
  const su = shiftUi || { state: "idle" };
  const shiftLabel = su.state === "working" ? "Podrž · odchod"
    : su.state === "ended" ? `Hotovo · ${hoursLabel(su.hours)}`
    : su.state === "draft" ? "Vyplň výkaz"
    : "Podrž · příchod";
  const shiftShort = su.state === "working" ? "Odchod" : su.state === "ended" ? "Hotovo" : su.state === "draft" ? "Výkaz" : "Příchod";
  const shiftAria = su.state === "working" ? `V práci ${elapsedHm(su.since)} — podrž pro odchod z práce`
    : su.state === "ended" ? `Dnes hotovo, ${hoursLabel(su.hours)} — podrž pro další příchod`
    : su.state === "draft" ? "Výkaz čeká na vyplnění — podrž a otevři ho"
    : "Příchod do práce — podrž tlačítko";
  // 199/202: dělník, subdodavatel a skladník mají stavbu vybranou předem na kartě
  // směny → podržení přihlásí rovnou na ni
  const onShiftHold = () => {
    if (manager) return nav("/?tab=osobni&shift=1");
    const pref = ["WORKER", "SUBCONTRACTOR", "WAREHOUSE"].includes(user?.role) ? storage.get(`pp-site-${user.id}`) : null;
    return nav(pref ? `/?shift=1&site=${encodeURIComponent(pref)}` : "/?shift=1");
  };
  const onShiftTap = () => toast.info(su.state === "working" ? "Podrž tlačítko pro odchod" : "Podrž tlačítko", { description: "Drž, dokud se kroužek nezaplní", duration: 2500 });
  const centerAction = accountant
    ? <CenterTapButton icon={Camera} label="Doklad" onClick={() => docPicker.open("faktura", { choose: true })} />
    : designer
    ? <CenterTapButton icon={BookOpen} label="Deník" onClick={() => nav("/denik")} />
    : <ShiftHoldButton state={su.state} hm={su.state === "working" ? elapsedHm(su.since) : ""} label={shiftLabel} shortLabel={shiftShort} ariaLabel={shiftAria}
        onHold={onShiftHold} onTap={onShiftTap} />;

  // 298: Vytvořit v levém sloupci — jen to, co daná role smí (úkol, zápis, fotky, doklad, zakázka).
  // Zatím skryté (majitel 19. 10.): zapne se, až budou podle laboratoře doladěné i ostatní stránky.
  const SHOW_CREATE = false;
  const [taskOpen, setTaskOpen] = useState(false);
  const galleryRole = ["OWNER", "MANAGER", "ASSISTANT", "WORKER"].includes(user?.role);
  const createItems = [
    ...(!accountant && user?.role !== "WAREHOUSE" ? [{ key: "task", label: "Úkol", sub: "s termínem, zakázkou a kdo", icon: CREATE_ICONS.task, onSelect: () => setTaskOpen(true) }] : []),
    ...(manager || designer ? [{ key: "diary", label: "Zápis do deníku", sub: "dnešní zápis na stavbě", icon: CREATE_ICONS.diary, onSelect: () => nav("/denik") }] : []),
    ...(galleryRole ? [{ key: "photos", label: "Fotky ze stavby", sub: "nahrát do galerie", icon: CREATE_ICONS.photos, onSelect: () => nav("/galerie") }] : []),
    ...(uploader ? [{ key: "doc", label: "Doklad", sub: "vyfotit a přečíst", icon: CREATE_ICONS.doc, onSelect: () => docPicker.open("faktura", { choose: true }) }] : []),
    ...(manager ? [{ key: "project", label: "Zakázka", sub: "nová poptávka", icon: CREATE_ICONS.project, onSelect: () => nav("/zakazky?nova=1") }] : []),
  ];

  // Menu B: navigace z menu nahradí jeho vlastní položku v historii (Zpět na Androidu)
  const menuGo = (to) => {
    const replace = !!(window.history.state && window.history.state.ppMenu);
    setMenuOpen(false);
    nav(to, { replace });
  };
  const openMenuB = (search) => { setMenuSearch(!!search); setMenuOpen(true); };

  return (
    <div className="relative isolate min-h-screen flex">
      {/* 298: teplé pozadí z laboratoře pod celou aplikací (i na mobilu) */}
      <div aria-hidden="true" className="pp-app-bg" />
      {/* 298: levý sloupec na PC — skleněná karta (laboratoř „Proyekty – Můj návrh“, menu C) */}
      <SideMenu user={user} company={company} slim={collapsed} onToggleSlim={toggleCollapsed} create={SHOW_CREATE ? createItems : []}
        footer={(
          <div className={`flex items-center gap-1 ${collapsed ? "flex-col" : ""}`}>
            {collapsed && <SideTip label="Upozornění"><span className="inline-flex"><NotificationBell /></span></SideTip>}
            <DropdownMenu>
              <SideTip label={`${user?.name || ""} · ${roleLabel(user?.role)}`} on={collapsed}>
              <DropdownMenuTrigger asChild>
                <button type="button" data-testid="user-menu-btn"
                  className={`flex items-center gap-2.5 rounded-[12px] hover:bg-foreground/5 transition-colors min-w-0 ${collapsed ? "p-1 justify-center" : "flex-1 px-1.5 py-1.5 text-left"}`}>
                  <Avatar name={user?.name} photo={user?.photo_url} size={36} className="pp-me-av" testid="sidebar-avatar" />
                  {!collapsed && (
                    <span className="min-w-0">
                      <span className="block text-[13.5px] font-semibold truncate">{user?.name}</span>
                      <span className="block text-xs text-[color:var(--pp-ink-2)] truncate">{roleLabel(user?.role)}</span>
                    </span>
                  )}
                </button>
              </DropdownMenuTrigger>
              </SideTip>
              <DropdownMenuContent side={collapsed ? "right" : "top"} align={collapsed ? "end" : "start"} sideOffset={collapsed ? 22 : 8} className="w-60">
                <DropdownMenuLabel className="min-w-0">
                  <span className="block text-sm font-semibold truncate">{user?.name}</span>
                  <span className="block text-xs font-normal text-muted-foreground truncate">{roleLabel(user?.role)} · {company?.name}</span>
                </DropdownMenuLabel>
                {/* Přepínač účtu je tady — dostupný zúženě i rozbaleně. */}
                <CompanySwitcher />
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/nastaveni")} data-testid="nav-settings" className="py-2.5">
                  <SettingsIcon size={16} className="mr-2" /> Nastavení
                </DropdownMenuItem>
                {hasGuide(user?.role) && (   // 308: průvodce mají všechny role
                  <DropdownMenuItem onClick={() => window.dispatchEvent(new Event("pp-guide-start"))} data-testid="nav-guide" className="py-2.5">
                    <Compass size={16} className="mr-2" /> {guideLabel(user?.role)}
                  </DropdownMenuItem>
                )}
                {user?.is_platform_admin && (
                  <DropdownMenuItem onClick={() => nav("/superadmin")} data-testid="nav-superadmin" className="py-2.5">
                    <Shield size={16} className="mr-2" /> Superadmin
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={toggle} data-testid="theme-toggle-btn" className="py-2.5">
                  {theme === "dark" ? <Sun size={16} className="mr-2" /> : <Moon size={16} className="mr-2" />}
                  {theme === "dark" ? "Světlý režim" : "Tmavý režim"}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={doLogout} data-testid="logout-btn" className="py-2.5 text-destructive focus:text-destructive">
                  <LogOut size={16} className="mr-2" /> Odhlásit se
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {!collapsed && <NotificationBell />}
          </div>
        )} />

      {/* Main */}
      <div className={`flex-1 ${collapsed ? "md:ml-[80px]" : "md:ml-[248px]"} min-w-0 flex flex-col min-h-screen`}>
        {/* Mobil (194): plovoucí skleněné ostrůvky místo plného pruhu + místo pro ně v obsahu */}
        <MobileHeader user={user} menuOpen={menuOpen}
          onProfile={() => setProfileOpen(true)}
          onSearch={() => openMenuB(true)}
          onScan={uploader ? () => docPicker.open("faktura", { choose: true }) : null}
          onMenu={() => openMenuB(false)} />
        <div aria-hidden="true" className="md:hidden shrink-0" style={{ height: "calc(max(14px, calc(env(safe-area-inset-top) + 6px)) + 44px)" }} />

        {user && user.email_verified === false && (
          <div data-testid="verify-email-banner"
               className="mx-4 mt-4 md:mx-6 rounded-[14px] border border-amber-500/40 bg-amber-500/10 px-4 py-3
                          flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <p className="text-sm text-amber-900 dark:text-amber-200 min-w-0">
              Potvrďte prosím svůj e-mail — bez toho nelze přepínat mezi firmami.
            </p>
            <button onClick={resendVerification} data-testid="verify-email-resend"
                    className="w-full md:w-auto shrink-0 h-10 px-4 rounded-[12px] font-semibold
                               bg-brand text-white hover:bg-brand/90 transition-colors">
              Poslat odkaz znovu
            </button>
          </div>
        )}

        <main className="flex-1 p-4 md:p-8 pb-[calc(7rem+env(safe-area-inset-bottom))] md:pb-8">
          {access?.payment_warning && user?.role === "OWNER" && (
            <a href="/nastaveni?tab=predplatne" data-testid="payment-warning-bar"
              className="flex flex-wrap items-center gap-2 rounded-[14px] border border-brand/40 bg-brand/10 px-4 py-2.5 mb-4 text-sm">
              <span className="font-semibold">
                Platbu se nepodařilo strhnout
                {access.payment_retry_until ? ` — zkusíme to znovu ${new Date(access.payment_retry_until).toLocaleDateString("cs-CZ")}` : ""}. Zkontrolujte platební kartu.
              </span>
              <span className="font-bold text-brand underline ml-auto shrink-0">Aktualizovat kartu</span>
            </a>
          )}
          {/* 384: při přechodu na jinou stránku se bloky zaostří postupně (jen PC, CSS .pp-page-in) */}
          <div ref={pageRef} className="pp-page contents">{children}</div>
        </main>

        {/* Mobil (194): Menu B přes celou obrazovku — moduly jako levý panel na PC */}
        <MenuB open={menuOpen} focusSearch={menuSearch} onClose={() => setMenuOpen(false)} onNavigate={menuGo} peopleSearch={manager}
          items={items} isItemActive={isItemActive} user={user}
          showSettings onSettings={() => menuGo("/nastaveni")}
          showSuperadmin={!!user?.is_platform_admin} onSuperadmin={() => menuGo("/superadmin")}
          dark={theme === "dark"} onToggleDark={toggle}
          onLogout={() => { setMenuOpen(false); doLogout(); }} />

        {/* Mobil (194): profil z avataru vlevo nahoře — firma, režim, nastavení, odhlášení.
            349: jediné místo pro účet na telefonu (avatar sem vede místo rovnou do Můj profil). */}
        <Sheet open={profileOpen} onOpenChange={setProfileOpen}>
          {/* p-0 by přes tailwind-merge sebral inset z varianty — odsazení proto přímo tady (135/B1) */}
          <SheetContent side="bottom" hideClose className="md:hidden rounded-t-[28px] bg-card border-0 px-4 pt-2.5 pb-[max(1.5rem,env(safe-area-inset-bottom))]" data-testid="mobile-profile-sheet">
            <div aria-hidden="true" className="w-10 h-[5px] rounded-full bg-border mx-auto mb-3" />
            <SheetHeader className="text-left space-y-0">
              <div className="flex items-center gap-3 min-w-0">
                <Avatar name={user?.name} photo={user?.photo_url} size={52} className="bg-brand text-white font-bold" testid="drawer-avatar" />
                <div className="min-w-0 flex-1">
                  <SheetTitle className="text-[19px] font-bold truncate">{user?.name}</SheetTitle>
                  <div className="text-[13px] text-muted-foreground truncate">{roleLabel(user?.role)} · {company?.name}</div>
                </div>
                <SheetClose asChild>
                  <button type="button" aria-label="Zavřít" data-testid="profile-close-btn"
                    className="h-11 w-11 shrink-0 rounded-full grid place-items-center bg-secondary text-muted-foreground">
                    <X size={19} />
                  </button>
                </SheetClose>
              </div>
            </SheetHeader>
            <CompanySwitcher variant="drawer" />
            <div className="mt-3 rounded-[20px] bg-secondary/60 overflow-hidden divide-y divide-border">
              <button type="button" onClick={() => { setProfileOpen(false); nav("/nastaveni?tab=profil"); }} data-testid="drawer-nav-profile"
                className="w-full h-[52px] px-4 flex items-center gap-3 text-left text-[15px] font-semibold active:bg-secondary">
                <UserRound size={18} className="text-muted-foreground" /> Můj profil
              </button>
              <button type="button" onClick={() => { setProfileOpen(false); nav("/nastaveni"); }} data-testid="drawer-nav-settings"
                className="w-full h-[52px] px-4 flex items-center gap-3 text-left text-[15px] font-semibold active:bg-secondary">
                <SettingsIcon size={18} className="text-muted-foreground" /> Nastavení
              </button>
              {hasGuide(user?.role) && (
                <button type="button" onClick={() => { setProfileOpen(false); window.dispatchEvent(new Event("pp-guide-start")); }} data-testid="drawer-nav-guide"
                  className="w-full h-[52px] px-4 flex items-center gap-3 text-left text-[15px] font-semibold active:bg-secondary">
                  <Compass size={18} className="text-muted-foreground" /> {guideLabel(user?.role)}
                </button>
              )}
              {user?.is_platform_admin && (
                <button type="button" onClick={() => { setProfileOpen(false); nav("/superadmin"); }} data-testid="drawer-nav-superadmin"
                  className="w-full h-[52px] px-4 flex items-center gap-3 text-left text-[15px] font-semibold active:bg-secondary">
                  <Shield size={18} className="text-muted-foreground" /> Superadmin
                </button>
              )}
              <button type="button" role="switch" aria-checked={theme === "dark"} onClick={toggle} data-testid="drawer-theme-btn"
                className="w-full h-[52px] px-4 flex items-center gap-3 text-left text-[15px] font-semibold active:bg-secondary">
                <Moon size={18} className="text-muted-foreground" /> <span className="flex-1">Tmavý režim</span>
                <span aria-hidden="true" className={`relative w-12 h-7 rounded-full shrink-0 transition-colors ${theme === "dark" ? "bg-success" : "bg-muted-foreground/30"}`}>
                  <span className={`absolute top-[3px] left-[3px] w-[22px] h-[22px] rounded-full bg-white shadow transition-transform ${theme === "dark" ? "translate-x-5" : ""}`} />
                </span>
              </button>
            </div>
            <button type="button" onClick={() => { setProfileOpen(false); doLogout(); }} data-testid="drawer-logout-btn"
              className="mt-3 w-full h-[52px] rounded-[20px] bg-secondary/60 flex items-center justify-center gap-2.5 text-[15px] font-bold text-destructive active:bg-secondary">
              <LogOut size={18} /> Odhlásit se
            </button>
            <AppVersionLine className="text-[11px] text-muted-foreground text-center pt-3" testid="drawer-app-version" />
          </SheetContent>
        </Sheet>

        {/* Mobil (194): skleněná plovoucí lišta, uprostřed docházka (podržením) */}
        <MobileBottomBar left={bLeft} right={bRight} center={centerAction} hidden={kbdOpen || menuOpen} />

        {/* FAB chooser (manager) — Nahrát fakturu / Nahrát účtenku */}
        <Sheet open={fabOpen} onOpenChange={setFabOpen}>
          <SheetContent side="bottom" className="rounded-t-[24px] bg-card safe-bottom" data-testid="fab-chooser">
            <SheetHeader className="text-left">
              <SheetTitle className="text-lg">Nahrát doklad</SheetTitle>
            </SheetHeader>
            <div className="grid grid-cols-2 gap-3 pt-4 pb-2">
              <button onClick={() => { docPicker.open("faktura"); setFabOpen(false); }} data-testid="fab-chooser-faktura"
                className="flex flex-col items-center justify-center gap-2 rounded-[20px] border border-border bg-secondary p-6 hover:border-brand transition-colors">
                <FileText size={30} className="text-brand" />
                <span className="font-bold">Nahrát fakturu</span>
                <span className="text-xs text-muted-foreground">Foto nebo PDF</span>
              </button>
              <button onClick={() => { docPicker.open("uctenka"); setFabOpen(false); }} data-testid="fab-chooser-uctenka"
                className="flex flex-col items-center justify-center gap-2 rounded-[20px] border border-border bg-secondary p-6 hover:border-brand transition-colors">
                <ReceiptText size={30} className="text-brand" />
                <span className="font-bold">Nahrát účtenku</span>
                <span className="text-xs text-muted-foreground">Foto paragonu</span>
              </button>
            </div>
          </SheetContent>
        </Sheet>

        {/* Global document upload overlay (FAB) — no navigation, returns to current page */}
        {docPicker.inputs}
        {docDrop.overlay}
        <DocUploadOverlay type={docUpload?.type || null} file={docUpload?.file || null} onClose={nextDropped} onSaved={() => window.dispatchEvent(new Event("pp-doc-saved"))} />
        {/* 298: nový úkol z tlačítka Vytvořit (stránka Úkoly se po založení sama obnoví) */}
        <NewTaskDialog hideTrigger open={taskOpen} onOpenChange={setTaskOpen} onCreated={() => window.dispatchEvent(new Event("pp-task-created"))} />
        {/* 242: průvodce majitele (otázka → plán → kroky na skutečných stránkách → certifikát) */}
        <OwnerGuide />
      </div>
    </div>
  );
}
